import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import IResponse from "./IResponse";

class Fetcher {
  private static URL = "https://deploy-vercel-deliceo.vercel.app/api";

  public static async makeRequest<T>(options: AxiosRequestConfig) {
    try {
      const response = await axios(options);

      return Fetcher.formatResponse<T>(response, true);
    } catch (error_) {
      const error = error_ as any;

      return this.handleError<T>(error as AxiosError);
    }
  }

  public static async get<T>(
    uri: string,
    params: object = {},
    headers: object = {},
    isAuth = true
  ): Promise<IResponse<T>> {
    // Build request
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, params),
      method: "get",
      headers: this.getFinalHeaders(headers, isAuth),
    };

    return this.makeRequest<T>(options);
  }

  public static async post<T>(
    uri: string,
    params: object = {},
    headers: object = {},
    isAuth = true
  ): Promise<IResponse<T>> {
    // Build request
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "post",
      headers: this.getFinalHeaders(headers, isAuth),
      data: params,
    };

    return this.makeRequest<T>(options);
  }

  public static async patch<T>(
    uri: string,
    params: object = {},
    headers: object = {},
    isAuth = true
  ): Promise<IResponse<T>> {
    // Build request
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "patch",
      headers: this.getFinalHeaders(headers, isAuth),
      data: params,
    };

    return this.makeRequest<T>(options);
  }

  public static async put<T>(
    uri: string,
    params: object = {},
    headers: object = {},
    isAuth = true
  ): Promise<IResponse<T>> {
    // Build request
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "put",
      headers: this.getFinalHeaders(headers, isAuth),
      data: params,
    };

    return this.makeRequest<T>(options);
  }

  public static async delete<T>(
    uri: string,
    params: object = {},
    headers: object = {},
    isAuth = true
  ): Promise<IResponse<T>> {
    // Build request
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "delete",
      headers: this.getFinalHeaders(headers, isAuth),
      data: params,
    };

    return this.makeRequest<T>(options);
  }

  private static getFinalHeaders(
    headers: object,
    isAuth: boolean
  ): { [x: string]: string } {
    let finalHeaders = { ...headers };

    if (isAuth) {
      finalHeaders = {
        Authorization: localStorage.getItem("authToken") ?? "nullToken",
        ...headers,
      };
    }
    return finalHeaders;
  }

  private static buildURL(pathname: string, search: object) {
    return this.URL + pathname + this.buildURLParamsFromObject(search);
  }

  private static isRequestSuccess = (code: number): boolean => {
    return code >= 200 && code < 300;
  };

  private static formatResponse<T>(
    axiosResponse: AxiosResponse<any, any>,
    noMessage = false
  ): IResponse<T> {
    if (noMessage) {
      return {
        ok: this.isRequestSuccess(axiosResponse.status),
        code: axiosResponse.status,
        data: axiosResponse.data.data,
      };
    }

    return {
      ok: this.isRequestSuccess(axiosResponse.status),
      message: axiosResponse.statusText,
      code: axiosResponse.status,
      data: axiosResponse.data.data,
    };
  }

  private static buildURLParamsFromObject(params: object): string {
    if (Object.keys(params).length > 0) {
      const strParams: Record<string, string> = {};
      for (const [key, value] of Object.entries(params)) {
        if (value?.toString()?.length > 0) {
          strParams[key] = value.toString();
        }
      }
      return "?" + new URLSearchParams(strParams).toString();
    }
    return "";
  }

  private static handleError<T>(error: AxiosError): Promise<IResponse<T>> {
    const message = (error.response?.data as any)?.error;

    if (error.response) {
      return Promise.reject({
        message,
        ok: false,
        code: error.response.status,
      }).then(
        (res) => res,
        (rej) => {
          if (rej.message === "Token empty or invalid")
            if (window.location.pathname !== "/disconnect")
              window.location.href = "/disconnect";

          return rej;
        }
      );
    }
    return Promise.reject({
      message: error.message,
      ok: false,
      code: 500,
    });
  }
}

export default Fetcher;
